import React, { useState, useEffect } from 'react'
import { PlusCircleIcon, TrashIcon, BriefcaseIcon } from 'lucide-react'
import { ApiService } from '../../../api/services.gen'
import { useAlert } from '../../../constants/AlertProvider'

export default function EditWorkExp({ applicationId }) {
    const [experiences, setExperiences] = useState([])
    const showAlert = useAlert();

    // Fetch existing experiences
    useEffect(() => {
        const fetchExperiences = async () => {
            if (!applicationId) return
            try {
                const res = await ApiService.apiExperienceList({ application: applicationId })
                setExperiences(res.results || res)
            } catch (e) {
                showAlert("Failed to load experiences", "error");
            }
        }
        fetchExperiences()
    }, [applicationId])

    // POST: Add new experience
    const addExperience = async () => {
        if (experiences.length >= 3) {
            showAlert("Only 3 experiences can be added", "error");
            return;
        }
        const newExp = {
            company: 'Company Name',
            title: 'Software Engineer',
            city: 'City',
            state: 'State',
            country: 'Country',
            is_remote: false,
            start_date: '2023-01-01',
            end_date: '2023-01-01',
            responsibilities: 'Job responsibilities',
            application: applicationId,
        }
        try {
            const res = await ApiService.apiExperienceCreate({ requestBody: newExp })
            setExperiences([...experiences, res])
        } catch (error) {
            showAlert("Failed to add experiences", "error");
            console.log("Failed to add experiences", error);
        }
    }

    // PATCH: Update experience
    const updateExperience = async (id, field, value) => {
        // Optimistically update the UI
        setExperiences(
            experiences.map((exp) =>
                exp.id === id ? { ...exp, [field]: value } : exp
            )
        );
        try {
            // Send only the updated field to the backend to avoid race conditions
            await ApiService.apiExperiencePartialUpdate({
                id,
                requestBody: { [field]: value },
            });
        } catch (e) {
            showAlert("Failed to update experiences", "error");
        }
    }

    // DELETE: Remove experience
    const removeExperience = async (id) => {
        setExperiences(experiences.filter((exp) => exp.id !== id))
        try {
            await ApiService.apiExperienceDestroy({ id })
        } catch (e) {
            showAlert("Failed to delete experiences", "error");
        }
    }

    return (
        <div className="space-y-8">
            {experiences.map((experience, index) => (
                <div
                    key={experience.id}
                    className="pt-6 first:pt-0 border-t first:border-t-0 border-gray-200 dark:border-gray-700"
                >
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100">
                            Experience {index + 1}
                        </h3>
                        {index > 0 && (
                            <button
                                onClick={() => removeExperience(experience.id)}
                                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center gap-1"
                            >
                                <TrashIcon size={16} />
                                Remove
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label
                                htmlFor={`title-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Job Title
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <BriefcaseIcon size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    id={`title-${experience.id}`}
                                    value={experience.title}
                                    onChange={(e) =>
                                        updateExperience(experience.id, 'title', e.target.value)
                                    }
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder='Software Engineer'
                                />
                            </div>
                        </div>
                        <div>
                            <label
                                htmlFor={`company-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Company
                            </label>
                            <input
                                type="text"
                                id={`company-${experience.id}`}
                                value={experience.company}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'company', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder='TechCorp Ltd.'
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`city-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                City
                            </label>
                            <input
                                type="text"
                                id={`city-${experience.id}`}
                                value={experience.city}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'city', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`state-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                State
                            </label>
                            <input
                                type="text"
                                id={`state-${experience.id}`}
                                value={experience.state}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'state', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`country-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Country
                            </label>
                            <input
                                type="text"
                                id={`country-${experience.id}`}
                                value={experience.country}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'country', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor={`startDate-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Start Date
                            </label>
                            <input
                                type="date"
                                id={`startDate-${experience.id}`}
                                value={experience.start_date}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'start_date', e.target.value)
                                }
                                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="relative">
                            <label
                                htmlFor={`endDate-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                End Date
                            </label>
                            <input
                                type="date"
                                id={`endDate-${experience.id}`}
                                value={experience.end_date}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'end_date', e.target.value)
                                }
                                className={`block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${experience.current ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
                            />
                        </div>
                        <div>
                            <div className="flex items-center mt-6">
                                <input
                                    id={`is_remote-${experience.id}`}
                                    type="checkbox"
                                    checked={experience.is_remote}
                                    onChange={(e) => {
                                        updateExperience(experience.id, 'is_remote', e.target.checked)
                                        if (e.target.checked) {
                                            updateExperience(experience.id, 'endDate', '')
                                        }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                                />
                                <label
                                    htmlFor={`is_remote-${experience.id}`}
                                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                                >
                                    This is a Remote Position
                                </label>
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label
                                htmlFor={`description-${experience.id}`}
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Responsibilities
                            </label>
                            <textarea
                                id={`responsibilities-${experience.id}`}
                                rows={4}
                                value={experience.responsibilities}
                                onChange={(e) =>
                                    updateExperience(experience.id, 'responsibilities', e.target.value)
                                }
                                className="block w-full px-6 py-4 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Describe your responsibilities and achievements"
                            />
                        </div>
                    </div>
                </div>
            ))}
            <div className="pt-4">
                <button
                    type="button"
                    onClick={addExperience}
                    // disabled={experiences.length >= 3}
                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                    <PlusCircleIcon size={20} className="mr-1" />
                    Add Work Experience
                </button>
            </div>
        </div>
    )
}
